import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { useMediaDeviceSelect, usePersistentUserChoices } from '@livekit/components-react'
import { MeetingsMeetingDataDocument } from 'generated/resources/graphql'
import {
  LocalTrack,
  LocalVideoTrack,
  Mutex,
  Track,
  VideoPresets,
  createLocalAudioTrack,
  createLocalVideoTrack,
  facingModeFromLocalTrack,
} from 'livekit-client'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { PrejoinSkeleton } from 'modules/meetings/components/Skeletons/PrejoinSkeleton'
import { useMeetingDetails } from 'modules/meetings/lib/hooks/useMeetingDetails'
import { useVirtualBackgroundSettings } from 'modules/meetings/lib/hooks/useVirtualBackgroundSettings'

import { PrejoinControlBar } from './components/PrejoinControlBar'
import { RoomInfo } from './components/RoomInfo'
import { VideoContainer } from './components/VideoContainer'

const MeetingPrejoinRoomPage = () => {
  const { id: meetingId } = useParams<{ id: string }>()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { loading: pageDetailsLoading } = useMeetingDetails()

  const { data, loading } = useQuery(MeetingsMeetingDataDocument, {
    variables: { id: Number(meetingId) },
  })

  const {
    access: { fullName: username, firstName, lastName, avatar },
  } = useCurrentSession()

  const {
    userChoices: initialUserChoices,
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices({
    defaults: {
      username,
    },
  })

  // Separate local states for better memory persistence, similar to RoomControlBar
  const [audioEnabled, setAudioEnabled] = useState(initialUserChoices.audioEnabled)
  const [videoEnabled, setVideoEnabled] = useState(initialUserChoices.videoEnabled)
  const [audioDeviceId, setAudioDeviceId] = useState(initialUserChoices.audioDeviceId)
  const [videoDeviceId, setVideoDeviceId] = useState(initialUserChoices.videoDeviceId)

  // Computed localUserChoices based on individual states
  const localUserChoices = useMemo(
    () => ({
      ...initialUserChoices,
      audioEnabled,
      videoEnabled,
      audioDeviceId,
      videoDeviceId,
    }),
    [initialUserChoices, audioEnabled, videoEnabled, audioDeviceId, videoDeviceId]
  )

  // Virtual background settings
  const { backgroundType, setBackgroundType } = useVirtualBackgroundSettings()

  // Use LiveKit hook for audio output device management with persistence
  const {
    devices: audioOutputDevices,
    activeDeviceId: activeAudioOutputId,
    setActiveMediaDevice: setActiveAudioOutputDevice,
  } = useMediaDeviceSelect({ kind: 'audiooutput' })

  const videoRef = useRef<HTMLVideoElement>(null)
  // Add a mutex for track creation to prevent race conditions
  const trackLock = useRef(new Mutex()).current

  const [tracks, setTracks] = useState<LocalTrack[]>([])

  // Use tracking for the audio track creation
  useEffect(() => {
    let needsCleanup = false
    const localTracks: LocalTrack[] = []

    trackLock.lock().then(async (unlock) => {
      try {
        // Find existing tracks
        const audioTrack = tracks.find((track) => track.kind === Track.Kind.Audio)

        // Stop existing audio track if present
        if (audioTrack) {
          audioTrack.stop()
        }

        // Create new audio track if enabled
        if (localUserChoices.audioEnabled) {
          const newAudioTrack = await createLocalAudioTrack({
            deviceId: localUserChoices.audioDeviceId || undefined,
          })
          localTracks.push(newAudioTrack)
        }

        // Retain existing video track if enabled
        const videoTrack = tracks.find((track) => track.kind === Track.Kind.Video)
        if (videoTrack && localUserChoices.videoEnabled) {
          localTracks.push(videoTrack)
        }

        if (!needsCleanup) {
          setTracks(localTracks)
        }
      } catch (e) {
      } finally {
        unlock()
      }
    })

    return () => {
      needsCleanup = true
      localTracks
        .filter((track) => track.kind === Track.Kind.Audio)
        .forEach((track) => track.stop())
    }
  }, [localUserChoices.audioEnabled, localUserChoices.audioDeviceId])

  // Use tracking for the video track creation
  useEffect(() => {
    let needsCleanup = false
    const localTracks: LocalTrack[] = []

    trackLock.lock().then(async (unlock) => {
      try {
        // Find existing tracks
        const audioTrack = tracks.find((track) => track.kind === Track.Kind.Audio)
        const videoTrack = tracks.find((track) => track.kind === Track.Kind.Video)

        // Stop existing video track if present
        if (videoTrack) {
          videoTrack.stop()
        }

        // Retain existing audio track if enabled
        if (audioTrack && localUserChoices.audioEnabled) {
          localTracks.push(audioTrack)
        }

        // Create new video track if enabled
        if (localUserChoices.videoEnabled) {
          const newVideoTrack = await createLocalVideoTrack({
            deviceId: localUserChoices.videoDeviceId || undefined,
            resolution: VideoPresets.h720.resolution,
          })
          localTracks.push(newVideoTrack)
        }

        if (!needsCleanup) {
          setTracks(localTracks)
        }
      } catch (e) {
      } finally {
        unlock()
      }
    })

    return () => {
      needsCleanup = true
      localTracks
        .filter((track) => track.kind === Track.Kind.Video)
        .forEach((track) => track.stop())
    }
  }, [localUserChoices.videoEnabled, localUserChoices.videoDeviceId])

  const _audioTrack = tracks.find((track) => track.kind === Track.Kind.Audio)
  const videoTrack = tracks.find((track) => track.kind === Track.Kind.Video)

  // Type guard to check if a LocalTrack is a LocalVideoTrack
  const isLocalVideoTrack = (track: LocalTrack): track is LocalVideoTrack => {
    return track.kind === Track.Kind.Video
  }

  // Additional effect to ensure background is applied when track or background changes
  useEffect(() => {
    if (videoTrack && backgroundType) {
      const applyBackgroundDirectly = async () => {
        try {
          // Import the function dynamically to avoid issues
          const { applyVirtualBackgroundToTrack } = await import(
            'modules/meetings/lib/utils/virtualBackgroundUtils'
          )

          // Add a delay to ensure track is ready
          setTimeout(async () => {
            // Type guard: ensure we have a video track before applying background
            if (videoTrack && isLocalVideoTrack(videoTrack)) {
              await applyVirtualBackgroundToTrack(videoTrack, backgroundType)
            }
          }, 200)
        } catch (error) {
          // Handle error silently
        }
      }

      applyBackgroundDirectly()
    }
  }, [videoTrack, backgroundType])

  // Get facing mode for camera
  const facingMode = useMemo(() => {
    if (videoTrack) {
      const { facingMode: facingMod } = facingModeFromLocalTrack(videoTrack)
      return facingMod
    }
    return 'undefined'
  }, [videoTrack])

  useEffect(() => {
    if (videoRef.current && videoTrack) {
      videoTrack.attach(videoRef.current)
    }

    return () => {
      if (videoTrack) {
        videoTrack.detach()
      }
    }
  }, [videoTrack])

  useEffect(() => {
    saveAudioInputEnabled(audioEnabled)
    saveVideoInputEnabled(videoEnabled)
    saveAudioInputDeviceId(audioDeviceId)
    saveVideoInputDeviceId(videoDeviceId)
  }, [
    audioEnabled,
    videoEnabled,
    audioDeviceId,
    videoDeviceId,
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  ])

  const handleMicToggle = async () => {
    setAudioEnabled(!audioEnabled)
  }

  const handleCameraToggle = async () => {
    setVideoEnabled(!videoEnabled)
  }

  const handleAudioInputChange = async (value: string) => {
    setAudioDeviceId(value)
  }

  const handleVideoInputChange = async (value: string) => {
    setVideoDeviceId(value)
  }

  const handleAudioOutputChange = async (value: string) => {
    if (setActiveAudioOutputDevice) {
      await setActiveAudioOutputDevice(value)
      // Save to localStorage for persistence across sessions
      localStorage.setItem('livekit-audio-output-device', value)
    }
  }

  const handleBackgroundChange = (value: string) => {
    setBackgroundType(value)
  }

  useEffect(() => {
    // Load saved audio output device from localStorage on mount
    const savedAudioOutputDeviceId = localStorage.getItem('livekit-audio-output-device')
    if (savedAudioOutputDeviceId && setActiveAudioOutputDevice) {
      setActiveAudioOutputDevice(savedAudioOutputDeviceId)
    }
  }, [setActiveAudioOutputDevice])

  const { devices: videoInputDevices } = useMediaDeviceSelect({ kind: 'videoinput' })
  const { devices: audioInputDevices } = useMediaDeviceSelect({ kind: 'audioinput' })

  if (loading || pageDetailsLoading) {
    return <PrejoinSkeleton />
  }

  return (
    <ResponsivePage.Body showHeader={false}>
      <Box
        position="absolute"
        style={{ inset: '0px', overflow: 'hidden' }}
        alignItems="center"
        justifyContent="center"
      >
        <Box
          flexDirection="row"
          style={{ width: '100%' }}
          alignItems="center"
          justifyContent="center"
        >
          <Box
            height="full"
            style={{
              maxWidth: '1024px',
              width: 'calc(100% - 300px)',
            }}
            flexDirection="column"
            gap="s12"
            position="relative"
            paddingRight="s64"
          >
            <VideoContainer
              videoTrack={videoTrack}
              localUserChoices={localUserChoices}
              firstName={firstName}
              lastName={lastName}
              avatar={avatar}
              username={username}
              videoRef={videoRef}
              facingMode={facingMode}
            />
            <PrejoinControlBar
              localUserChoices={localUserChoices}
              handleMicToggle={handleMicToggle}
              handleCameraToggle={handleCameraToggle}
              handleAudioInputChange={handleAudioInputChange}
              handleVideoInputChange={handleVideoInputChange}
              handleAudioOutputChange={handleAudioOutputChange}
              handleBackgroundChange={handleBackgroundChange}
              audioInputDevices={audioInputDevices}
              videoInputDevices={videoInputDevices}
              audioOutputDevices={audioOutputDevices}
              audioOutputDeviceId={activeAudioOutputId || ''}
              backgroundType={backgroundType || 'disabled'}
            />
          </Box>
          <RoomInfo
            title={data?.meetings.meeting?.title ?? undefined}
            description={data?.meetings.meeting?.description ?? undefined}
            handleJoin={() => {
              navigate(
                // TODO: change this with a hook that retrives the meeting type
                navigation.engagement.one_on_ones.meeting.meetId.room.fullPath({
                  id: Number(meetingId),
                })
              )
            }}
            handleLeave={() => {
              navigate(
                // TODO: change this with a hook that retrives the meeting type
                navigation.engagement.one_on_ones.meeting.meetId.fullPath({ id: Number(meetingId) })
              )
            }}
          />
        </Box>
      </Box>
    </ResponsivePage.Body>
  )
}

export default MeetingPrejoinRoomPage
